import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    TextField,
    Paper,
    Chip,
    CircularProgress,
    Tooltip,
    IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FilterListIcon from '@mui/icons-material/FilterList';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { FolderStateIcons } from './FolderStateIcons';
import { AssetTableProps, Asset, FolderTreeResponse, SortField, folderDisplayName } from '../AssetExplorer.types';
import { formatFileSize } from '../AssetExplorer.api';
import {
    assetExplorerFolderIconColor,
    assetExplorerTypeChipSx,
    isProfessionalLightTheme,
} from '../assetExplorerTheme';
import { PublishedAssetGlobeButton } from './PublishedAssetGlobeButton';

const getAssetIcon = (type: string, size: number = 24) => {
    const iconProps = { sx: { fontSize: size } };

    switch (type) {
        case 'IMAGE':
            return <ImageIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4CAF50' }} />;
        case 'VIDEO':
            return <VideoFileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#2196F3' }} />;
        case 'AUDIO':
            return <AudioFileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#9C27B0' }} />;
        default:
            return <InsertDriveFileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#9E9E9E' }} />;
    }
};

const getTypeChipColor = (type: string): 'success' | 'primary' | 'secondary' | 'warning' | 'default' | 'info' => {
    switch (type) {
        case 'IMAGE':
            return 'success';
        case 'VIDEO':
            return 'primary';
        case 'AUDIO':
            return 'secondary';
        case 'ZIP':
            return 'warning';
        case 'DOCUMENT':
            return 'info';
        default:
            return 'default';
    }
};

interface ColumnConfig {
    id: SortField | 'select' | 'publishedUrl' | 'actions' | 'status';
    label: string;
    width?: number | string;
    sortable: boolean;
    filterable: boolean;
}

const columns: ColumnConfig[] = [
    { id: 'select', label: '', width: 50, sortable: false, filterable: false },
    { id: 'publishedUrl', label: '', width: 48, sortable: false, filterable: false },
    { id: 'name', label: 'Name', width: '30%', sortable: true, filterable: true },
    { id: 'type', label: 'Type', width: 120, sortable: true, filterable: true },
    { id: 'status', label: 'Status', width: 100, sortable: false, filterable: false },
    { id: 'size', label: 'Size', width: 100, sortable: true, filterable: false },
    { id: 'createdAt', label: 'Uploaded', width: 180, sortable: true, filterable: false },
    { id: 'updatedAt', label: 'Last Modified', width: 180, sortable: true, filterable: false },
];

export const AssetTable: React.FC<AssetTableProps> = ({
    childFolders = [],
    onFolderOpen,
    selectedFolderIds = new Set(),
    onFolderToggleSelect,
    assets,
    selectedAssets,
    onAssetSelect,
    onSelectAllDisplayed,
    sortConfig,
    onSortChange,
    filterConfig,
    onFilterChange,
    loading,
}) => {
    const theme = useTheme();
    const [showFilters, setShowFilters] = React.useState(false);
    const [nameFilter, setNameFilter] = React.useState('');
    const [typeFilter, setTypeFilter] = React.useState('');

    const handleNameFilterChange = (value: string) => {
        setNameFilter(value);
        onFilterChange({ ...filterConfig, searchQuery: value || undefined });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    const folderMatchesColumnFilters = (folder: FolderTreeResponse) => {
        if (nameFilter && !folderDisplayName(folder).toLowerCase().includes(nameFilter.toLowerCase())) {
            return false;
        }
        if (typeFilter && !'folder'.includes(typeFilter.toLowerCase())) {
            return false;
        }
        return true;
    };

    const visibleFolders = childFolders.filter(folderMatchesColumnFilters);

    const filteredAssetsForRows = assets.filter((asset) => {
        if (nameFilter && !asset.name.toLowerCase().includes(nameFilter.toLowerCase())) {
            return false;
        }
        if (typeFilter && !asset.type.toLowerCase().includes(typeFilter.toLowerCase())) {
            return false;
        }
        return true;
    });

    const handleHeaderSelectAll = () => {
        const fIds = visibleFolders.map((f) => f._id);
        const aIds = filteredAssetsForRows.map((a) => a._id);
        const total = fIds.length + aIds.length;
        let sel = 0;
        fIds.forEach((id) => {
            if (selectedFolderIds.has(id)) sel++;
        });
        aIds.forEach((id) => {
            if (selectedAssets.has(id)) sel++;
        });
        if (total > 0 && sel === total) {
            onSelectAllDisplayed([], []);
        } else {
            onSelectAllDisplayed(fIds, aIds);
        }
    };

    let selectedVisibleCount = 0;
    visibleFolders.forEach((f) => {
        if (selectedFolderIds.has(f._id)) selectedVisibleCount++;
    });
    filteredAssetsForRows.forEach((a) => {
        if (selectedAssets.has(a._id)) selectedVisibleCount++;
    });
    const totalVisibleRows = visibleFolders.length + filteredAssetsForRows.length;
    const allSelected = totalVisibleRows > 0 && selectedVisibleCount === totalVisibleRows;
    const someSelected = selectedVisibleCount > 0 && selectedVisibleCount < totalVisibleRows;

    if (visibleFolders.length === 0 && filteredAssetsForRows.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                    color: theme.palette.text.secondary,
                }}
            >
                <InsertDriveFileIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" color="textSecondary">
                    This folder is empty
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Add subfolders from the tree or upload assets above
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Filter Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, px: 1 }}>
                <Tooltip title={showFilters ? 'Hide Filters' : 'Show Filters'}>
                    <IconButton 
                        size="small" 
                        onClick={() => setShowFilters(!showFilters)}
                        color={showFilters ? 'primary' : 'default'}
                    >
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        {/* Header Row */}
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sx={{
                                        width: column.width,
                                        fontWeight: 600,
                                        backgroundColor: theme.palette.background.paper,
                                    }}
                                >
                                    {column.id === 'select' ? (
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={someSelected}
                                            onChange={handleHeaderSelectAll}
                                            size="small"
                                        />
                                    ) : column.sortable ? (
                                        <TableSortLabel
                                            active={sortConfig.field === column.id}
                                            direction={sortConfig.field === column.id ? sortConfig.order : 'asc'}
                                            onClick={() => onSortChange(column.id as SortField)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : (
                                        column.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Filter Row */}
                        {showFilters && (
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={`filter-${column.id}`}
                                        sx={{
                                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                                            py: 0.5,
                                        }}
                                    >
                                        {column.id === 'name' && (
                                            <TextField
                                                size="small"
                                                placeholder="Filter by name..."
                                                value={nameFilter}
                                                onChange={(e) => handleNameFilterChange(e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        py: 0.5,
                                                        fontSize: '0.8rem',
                                                    },
                                                }}
                                            />
                                        )}
                                        {column.id === 'type' && (
                                            <TextField
                                                size="small"
                                                placeholder="Filter type..."
                                                value={typeFilter}
                                                onChange={(e) => setTypeFilter(e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        py: 0.5,
                                                        fontSize: '0.8rem',
                                                    },
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                    </TableHead>

                    <TableBody>
                        {onFolderOpen &&
                            onFolderToggleSelect &&
                            visibleFolders.map((folder) => {
                                const folderSelected = selectedFolderIds.has(folder._id);
                                return (
                                <TableRow
                                    key={`folder-${folder._id}`}
                                    hover
                                    selected={folderSelected}
                                    onClick={() => onFolderOpen(folder)}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: alpha(theme.palette.secondary.main, 0.06),
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.14),
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.18),
                                        },
                                    }}
                                >
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={folderSelected}
                                            onChange={() => onFolderToggleSelect(folder._id)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()} />
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FolderOutlinedIcon sx={{ fontSize: 24, color: assetExplorerFolderIconColor(theme) }} />
                                            <Tooltip title={folderDisplayName(folder)}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        maxWidth: 250,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        color:
                                                            theme.palette.mode === 'light'
                                                                ? alpha(theme.palette.grey[900], 0.94)
                                                                : theme.palette.secondary.dark,
                                                    }}
                                                >
                                                    {folderDisplayName(folder)}
                                                </Typography>
                                            </Tooltip>
                                            <FolderStateIcons folder={folder} size={15} />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label="Folder"
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                            —
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                            {folder.assetCount != null ? `${folder.assetCount} asset${folder.assetCount === 1 ? '' : 's'}` : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                            {formatDate(folder.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="textSecondary">
                                            {formatDate(folder.updatedAt)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        {filteredAssetsForRows.map((asset) => {
                                const isSelected = selectedAssets.has(asset._id);
                                return (
                                    <TableRow
                                        key={asset._id}
                                        hover
                                        selected={isSelected}
                                        onClick={() => onAssetSelect(asset._id)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&.Mui-selected': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            },
                                            '&.Mui-selected:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                            },
                                        }}
                                    >
                                        {/* Checkbox */}
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => onAssetSelect(asset._id)}
                                                size="small"
                                            />
                                        </TableCell>

                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <PublishedAssetGlobeButton publishedRelativeUrl={asset.publishedRelativeUrl} />
                                        </TableCell>

                                        {/* Name */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {asset.type === 'IMAGE' && asset.s3Url ? (
                                                    <Box
                                                        component="img"
                                                        src={asset.s3Url}
                                                        alt={asset.name}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            objectFit: 'cover',
                                                            borderRadius: 0.5,
                                                        }}
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                        }}
                                                    />
                                                ) : (
                                                    getAssetIcon(asset.type)
                                                )}
                                                <Box>
                                                    <Tooltip title={asset.name}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                maxWidth: 250,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {asset.name}
                                                        </Typography>
                                                    </Tooltip>
                                                    {asset.uploadedByEmail && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            by {asset.uploadedByEmail}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {/* Type */}
                                        <TableCell>
                                            <Chip
                                                label={asset.type}
                                                size="small"
                                                color={
                                                    isProfessionalLightTheme(theme) && asset.type === 'VIDEO'
                                                        ? undefined
                                                        : getTypeChipColor(asset.type)
                                                }
                                                variant={
                                                    isProfessionalLightTheme(theme) && asset.type === 'VIDEO'
                                                        ? 'outlined'
                                                        : 'filled'
                                                }
                                                sx={assetExplorerTypeChipSx(theme, asset.type)}
                                            />
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>
                                            <Chip
                                                label={
                                                    asset.publishedRelativeUrl && String(asset.publishedRelativeUrl).trim()
                                                        ? 'Publish'
                                                        : 'Draft'
                                                }
                                                size="small"
                                                color={
                                                    asset.publishedRelativeUrl && String(asset.publishedRelativeUrl).trim()
                                                        ? 'success'
                                                        : 'default'
                                                }
                                                variant={
                                                    asset.publishedRelativeUrl && String(asset.publishedRelativeUrl).trim()
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                                sx={{ fontWeight: 600, fontSize: '0.68rem' }}
                                            />
                                        </TableCell>

                                        {/* Size */}
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {formatFileSize(asset.size)}
                                            </Typography>
                                        </TableCell>

                                        {/* Uploaded */}
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {formatDate(asset.createdAt)}
                                            </Typography>
                                        </TableCell>

                                        {/* Last Modified */}
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {formatDate(asset.updatedAt)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default AssetTable;
